import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useDashboard } from '../lib/hooks';
import { errorText } from '../lib/errors';
import { cleanFlat, fmtDateTime, isFlat, isIndianMobile, normalizePhone } from '../lib/format';
import { useToast } from '../lib/toast';
import type { FamilyInbox, FamilyRequest, LinkedVia } from '../lib/types';
import { ResidentBar } from '../components/resident';
import { ErrorNote, Spinner } from '../components/ui';
import { AlertsSetting } from '../components/alerts';
import { isEmail } from '../components/EmailCode';
import { familyInviteText } from '../components/invite';
import { TOWERS } from './LinkFlat';

export const LINKED_LABEL: Record<LinkedVia, string> = {
  self: 'Started the family',
  utr: 'Booked before (mobile + UTR)',
  committee: 'Added by the Food Committee',
  family: 'Added by family',
  request: 'Asked to join, family said yes',
};

export function passesLink(token: string) {
  return `${location.origin}/f/${token}`;
}

export default function Account() {
  const { me, refreshMe, signOut } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const dash = useDashboard();
  const family = useQuery({ queryKey: ['family'], queryFn: api.family });
  const inbox = useQuery({ queryKey: ['family-inbox'], queryFn: api.familyInbox, refetchInterval: 30_000 });

  const [name, setName] = useState(me?.member?.name ?? '');
  const [phone, setPhone] = useState(me?.member?.phone ?? '');
  const [detailsErr, setDetailsErr] = useState<string | null>(null);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [addErr, setAddErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmReissue, setConfirmReissue] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);

  const label = me?.flat?.label ?? '';
  const token = dash.data?.flat.access_token;
  const link = token ? passesLink(token) : '';

  async function saveDetails() {
    setDetailsErr(null);
    const d = normalizePhone(phone);
    if (name.trim().length < 2) return setDetailsErr(errorText('name_invalid'));
    if (d && !isIndianMobile(d)) return setDetailsErr(errorText('phone_invalid'));
    setBusy('details');
    try {
      await api.saveMember(name, d);
      await refreshMe();
      toast('Saved');
    } catch (e) {
      setDetailsErr(errorText(e));
    } finally {
      setBusy(null);
    }
  }

  async function toggleNotify(on: boolean) {
    setBusy('notify');
    try {
      await api.setNotify(on);
      await refreshMe();
      await qc.invalidateQueries({ queryKey: ['family'] });
    } catch (e) {
      toast(errorText(e), { tone: 'bad' });
    } finally {
      setBusy(null);
    }
  }

  async function add() {
    setAddErr(null);
    if (!isEmail(newEmail)) return setAddErr(errorText('email_invalid'));
    setBusy('add');
    try {
      qc.setQueryData(['family'], await api.familyAdd(newEmail.trim().toLowerCase(), newName));
      toast(`Added ${newEmail.trim()} and sent them a welcome email.`);
      setNewEmail('');
      setNewName('');
    } catch (e) {
      setAddErr(errorText(e));
    } finally {
      setBusy(null);
    }
  }

  async function remove(email: string) {
    setBusy(email);
    try {
      qc.setQueryData(['family'], await api.familyRemove(email));
      toast(`Removed ${email}`);
    } catch (e) {
      toast(errorText(e), { tone: 'bad' });
    } finally {
      setBusy(null);
    }
  }

  async function reissue() {
    setBusy('reissue');
    try {
      qc.setQueryData(['dashboard'], await api.reissueMine());
      setConfirmReissue(false);
      toast('New QR codes issued. The old ones no longer work; the new ones are on My passes and in your email.');
    } catch (e) {
      toast(errorText(e), { tone: 'bad' });
    } finally {
      setBusy(null);
    }
  }

  async function leave() {
    setBusy('leave');
    try {
      await api.familyLeave();
      await refreshMe();
      qc.clear();
      navigate('/link', { replace: true });
    } catch (e) {
      toast(errorText(e), { tone: 'bad' });
      setBusy(null);
    }
  }

  // A one-time join link for family, shared on WhatsApp. The tab opens inside the tap so it isn't blocked as a pop-up.
  async function shareInvite(how: 'whatsapp' | 'copy') {
    setBusy('invite');
    const tab = how === 'whatsapp' ? window.open('', '_blank') : null;
    try {
      const text = familyInviteText(await api.familyInvite());
      if (how === 'copy') {
        await navigator.clipboard.writeText(text).then(() => toast('Join link copied. It works once, for 7 days.'), () => toast(text));
      } else {
        const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
        if (tab) tab.location.href = url;
        else location.href = url;
      }
    } catch (e) {
      tab?.close();
      toast(errorText(e), { tone: 'bad' });
    } finally {
      setBusy(null);
    }
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(link);
      toast('Link copied');
    } catch {
      toast(`Copy failed. The link is ${link}`);
    }
  }

  const notifyOn = me?.member?.notify !== false;
  const waText = `ONE10 meal passes for flat ${label}: ${link}\nShow the QR at the food counter.`;

  return (
    <div className="shell">
      <ResidentBar back={{ to: '/', label: 'My passes' }} title={`My family · ${label}`} />
      <main className="app-body">
        {inbox.data && inbox.data.requests.length > 0 && <Requests inbox={inbox.data} />}
        <form className="card stack" onSubmit={(e) => { e.preventDefault(); void saveDetails(); }}>
          <p className="eyebrow">Your details</p>
          <div>
            <label className="fld-l" htmlFor="name">Name</label>
            <input id="name" className="fld" autoComplete="name" maxLength={80} value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <label className="fld-l" htmlFor="phone">Mobile number</label>
            <div className="phone-in"><span>+91</span>
              <input id="phone" inputMode="numeric" autoComplete="tel-national" placeholder="98765 43210" maxLength={14}
                value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <p className="fine">The Food Committee uses it if they need to reach you about a payment.</p>
          </div>
          <ErrorNote error={detailsErr} />
          <button className="btn primary" style={{ justifySelf: 'start' }} disabled={busy === 'details'}>{busy === 'details' ? 'Saving…' : 'Save'}</button>
        </form>

        <section className="card stack">
          <div className="setting" style={{ padding: 0, border: 0 }}>
            <div><b>Email me updates</b><p>Payment confirmed or sent back, with your meal passes attached. Sent to {me?.email}.</p></div>
            <button className="switch" role="switch" aria-checked={notifyOn} aria-label="Email me updates" disabled={busy === 'notify'}
              onClick={() => void toggleNotify(!notifyOn)} />
          </div>
        </section>

        <AlertsSetting />

        <section className="card stack">
          <p className="eyebrow">Your family at {label}</p>
          <p className="fine">Everyone here can sign in, book for the family and see its passes. Other families at {label} can't.</p>
          {family.isPending ? <Spinner /> : family.isError ? <p className="err">{errorText(family.error)}</p> : (
            <ul className="mlist">
              {(family.data ?? []).map((m) => (
                <li key={m.email}>
                  <span>
                    <b>{m.email}{m.is_me && <span className="chip ok">You</span>}</b>
                    <small>{[m.name, LINKED_LABEL[m.linked_via], m.notify ? null : 'No emails'].filter(Boolean).join(' · ')}</small>
                  </span>
                  {!m.is_me && <button className="btn ghost sm" disabled={busy === m.email} onClick={() => void remove(m.email)}>Remove</button>}
                </li>
              ))}
            </ul>
          )}
          <form className="stack" style={{ gap: 8 }} onSubmit={(e) => { e.preventDefault(); void add(); }}>
            <label className="fld-l" htmlFor="newEmail" style={{ margin: 0 }}>Add a family member's email</label>
            <div className="row">
              <input id="newEmail" className="fld" style={{ flex: '2 1 200px' }} type="email" inputMode="email" placeholder="name@example.com"
                value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
              <input className="fld" style={{ flex: '1 1 120px' }} aria-label="Their name (optional)" placeholder="Name (optional)"
                value={newName} onChange={(e) => setNewName(e.target.value)} />
              <button className="btn primary" disabled={busy === 'add'}>{busy === 'add' ? 'Adding…' : 'Add'}</button>
            </div>
            <ErrorNote error={addErr} />
          </form>
          <div className="stack" style={{ gap: 8 }}>
            <p className="fld-l" style={{ margin: 0 }}>Or send them a join link</p>
            <p className="fine">They tap it, add their own email, and they're in. It works once, for 7 days; a new link replaces the last one.</p>
            <div className="row">
              <button className="btn ghost sm" disabled={busy === 'invite'} onClick={() => void shareInvite('whatsapp')}>Share on WhatsApp</button>
              <button className="btn ghost sm" disabled={busy === 'invite'} onClick={() => void shareInvite('copy')}>Copy join link</button>
            </div>
          </div>
        </section>

        {inbox.data && <FlatCard inbox={inbox.data} />}

        <section className="card stack">
          <p className="eyebrow">Passes link</p>
          <p className="fine">
            For family who don't sign in: this link opens the family's passes without a password, including the family pass that works at every
            meal. Share it only with family. For a guest, share a single-seat pass from the meal instead. The counter never serves more seats
            than you booked.
          </p>
          {token ? (
            <>
              <p className="mono" style={{ wordBreak: 'break-all', fontSize: 13 }}>{link}</p>
              <div className="row">
                <button className="btn ghost sm" onClick={() => void copy()}>Copy link</button>
                <a className="btn ghost sm" href={`https://wa.me/?text=${encodeURIComponent(waText)}`} target="_blank" rel="noreferrer">Share on WhatsApp</a>
              </div>
            </>
          ) : <Spinner />}
        </section>

        <section className="card stack">
          <p className="eyebrow">Lost a printout, or a QR went to the wrong people?</p>
          <p className="fine">
            Issue new QR codes: the family pass, meal passes, single-seat passes and the passes link all change, and the old ones stop working
            at once, including screenshots and printouts. Seats already served stay served.
          </p>
          {confirmReissue ? (
            <div className="notice warn">
              <p><b>Issue new QR codes for flat {label}?</b> Family using the old link or old printouts will need the new ones. We email
                them to everyone in the flat who gets updates.</p>
              <div className="row">
                <button className="btn danger sm" disabled={busy === 'reissue'} onClick={() => void reissue()}>
                  {busy === 'reissue' ? 'Issuing…' : 'Yes, issue new QR codes'}</button>
                <button className="btn ghost sm" onClick={() => setConfirmReissue(false)}>Keep the current ones</button>
              </div>
            </div>
          ) : (
            <button className="btn ghost sm" style={{ justifySelf: 'start' }} onClick={() => setConfirmReissue(true)}>Issue new QR codes</button>
          )}
        </section>

        <section className="card stack">
          <p className="eyebrow">Not part of this family?</p>
          <p className="fine">
            Leave it, and then start your own family or ask to join another. The family keeps its bookings and passes, and you no longer
            see them.
          </p>
          {confirmLeave ? (
            <div className="notice warn">
              <p><b>Leave this family at {label}?</b> To come back, someone in it adds your email again.</p>
              <div className="row">
                <button className="btn danger sm" disabled={busy === 'leave'} onClick={() => void leave()}>{busy === 'leave' ? 'Leaving…' : 'Yes, leave'}</button>
                <button className="btn ghost sm" onClick={() => setConfirmLeave(false)}>Stay</button>
              </div>
            </div>
          ) : (
            <button className="btn ghost sm" style={{ justifySelf: 'start' }} onClick={() => setConfirmLeave(true)}>Leave this family</button>
          )}
        </section>

        <p className="fine center">Signed in as {me?.email} · <button className="link-btn" onClick={() => void signOut().then(() => navigate('/signin'))}>Sign out</button></p>
      </main>
    </div>
  );
}

/** Requests to join this family, or to combine another family at the flat with it. */
function Requests({ inbox }: { inbox: FamilyInbox }) {
  const { me, refreshMe } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const label = me?.flat?.label ?? '';

  async function answer(r: FamilyRequest, yes: boolean) {
    setBusy(r.id);
    try {
      qc.setQueryData(['family-inbox'], await api.answerRequest(r.id, yes));
      await Promise.all([refreshMe(), qc.invalidateQueries({ queryKey: ['family'] }), qc.invalidateQueries({ queryKey: ['dashboard'] }),
        qc.invalidateQueries({ queryKey: ['history'] })]);
      toast(!yes ? 'Said no. They are not added.'
        : r.kind === 'join' ? `${r.name ?? r.email} is now in your family.`
        : 'The two families are now one. Everyone gets the combined passes by email.');
    } catch (e) {
      toast(errorText(e), { tone: 'bad' });
      void qc.invalidateQueries({ queryKey: ['family-inbox'] });
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="card stack req-card">
      <p className="eyebrow">Waiting for your answer</p>
      {inbox.requests.map((r) => (
        <div key={r.id} className="req">
          {r.kind === 'join' ? (
            <p><b>{r.name ?? r.email}</b> ({r.email}) asks to join your family at {label}. Once in, they see your bookings and passes
              and can book for the family.</p>
          ) : (
            <p>Another family at {label}, <b>{r.family?.name}</b> ({r.family?.members} {r.family?.members === 1 ? 'person' : 'people'},
              {' '}{r.family?.orders} {r.family?.orders === 1 ? 'booking' : 'bookings'}), asks to combine with yours: one family, its bookings and
              passes together under one QR. Asked by {r.name ?? r.email}.</p>
          )}
          <p className="fine">{fmtDateTime(r.created_at)}</p>
          <div className="row">
            <button className="btn primary sm" disabled={!!busy} onClick={() => void answer(r, true)}>
              {r.kind === 'join' ? 'Let them in' : 'Combine our families'}</button>
            <button className="btn ghost sm" disabled={!!busy} onClick={() => void answer(r, false)}>Not our family</button>
          </div>
        </div>
      ))}
      <p className="fine">Not sure who it is? Say no: they can still start their own family. If someone joined your flat by mistake, tell the help desk.</p>
    </section>
  );
}

/** The family's flat: change it (until it has bookings for an event that's on), and combine with another family there. */
function FlatCard({ inbox }: { inbox: FamilyInbox }) {
  const { me, refreshMe } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const f = me?.flat;
  const [editing, setEditing] = useState(false);
  const [tower, setTower] = useState(String(f?.tower ?? ''));
  const [flat, setFlat] = useState(f?.flat ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (!f) return null;
  const out = inbox.outgoing;
  const incomingMerge = inbox.requests.some((r) => r.kind === 'merge');

  async function save() {
    setError(null);
    if (!isFlat(flat)) return setError(errorText('flat_invalid'));
    setBusy(true);
    try {
      await api.familySetFlat(Number(tower), cleanFlat(flat));
      await refreshMe();
      await qc.invalidateQueries({ queryKey: ['family-inbox'] });
      setEditing(false);
      toast('Flat changed');
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  async function combine() {
    setBusy(true);
    try {
      qc.setQueryData(['family-inbox'], await api.mergeRequest());
      toast('Asked. They see it under My family and get an email.');
    } catch (e) {
      toast(errorText(e), { tone: 'bad' });
    } finally {
      setBusy(false);
    }
  }

  async function withdraw() {
    setBusy(true);
    try {
      await api.cancelRequest();
      await refreshMe();
      await qc.invalidateQueries({ queryKey: ['family-inbox'] });
    } catch (e) {
      toast(errorText(e), { tone: 'bad' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card stack">
      <p className="eyebrow">Flat {f.label}</p>
      {inbox.moved_out && (
        <div className="notice warn"><p>The help desk marked your family as moved out of {f.label}. If you live somewhere else now, change the flat
          below. Booking again also clears it.</p></div>
      )}
      {editing ? (
        <form className="stack" style={{ gap: 8 }} onSubmit={(e) => { e.preventDefault(); void save(); }}>
          <div className="row">
            <select className="fld" style={{ width: 'auto' }} aria-label="Tower" value={tower} onChange={(e) => setTower(e.target.value)}>
              {TOWERS.map((t) => <option key={t} value={t}>Tower {t}</option>)}
            </select>
            <input className="fld" style={{ flex: '1 1 90px' }} aria-label="Flat" placeholder="e.g. 3B" maxLength={4} autoComplete="off"
              value={flat} onChange={(e) => setFlat(cleanFlat(e.target.value))} />
          </div>
          <ErrorNote error={error} />
          <div className="row">
            <button className="btn primary sm" disabled={busy}>{busy ? 'Saving…' : 'Save flat'}</button>
            <button type="button" className="btn ghost sm" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </form>
      ) : inbox.can_move ? (
        <button className="btn ghost sm" style={{ justifySelf: 'start' }} onClick={() => setEditing(true)}>Wrong flat, or moved? Change it</button>
      ) : (
        <p className="fine">Wrong flat, or moved? Your family has bookings for an event that is on, so ask the help desk to change it: the free
          Bhog seats are counted per flat.</p>
      )}
      {inbox.others > 0 && (
        <div className="stack" style={{ gap: 8 }}>
          <p className="fine">
            {inbox.others === 1 ? 'Another family' : `${inbox.others} other families`} at {f.label} {inbox.others === 1 ? 'uses' : 'use'} the
            portal. If you live together, combine into one family: bookings and passes come together under one QR, and your current QR codes
            keep working.
          </p>
          {out?.status === 'pending' ? (
            <div className="notice">
              <p>Waiting for the other family to answer (asked {fmtDateTime(out.created_at)}).</p>
              <button className="link-btn" style={{ justifySelf: 'start' }} disabled={busy} onClick={() => void withdraw()}>Withdraw</button>
            </div>
          ) : out?.status === 'declined' ? (
            <div className="notice warn">
              <p>The other family said no. If someone joined your flat by mistake, tell the help desk.</p>
              <button className="link-btn" style={{ justifySelf: 'start' }} disabled={busy} onClick={() => void withdraw()}>OK</button>
            </div>
          ) : !incomingMerge && (
            <button className="btn ghost sm" style={{ justifySelf: 'start' }} disabled={busy} onClick={() => void combine()}>
              Ask to combine our families</button>
          )}
        </div>
      )}
    </section>
  );
}
